/**
 * 펀드 매니저 위젯 — 대시보드용 (Phase 4)
 * 국면 배지 + 오늘의 발굴 TOP5 + 데일리 브리핑 요약
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Button, Card, Spin, Tag, Typography, message } from "antd";
import { ArrowRightOutlined, ReloadOutlined, RobotOutlined } from "@ant-design/icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { apiClient } from "src/api/client";
import { marketDeskApi, screenerApi } from "src/api/market";

/**
 * LLM 마크다운 줄바꿈 정규화 (StockInsightCard와 동일 로직) — 2차 리뷰 #7
 */
const normalizeMarkdown = (md: string): string => {
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const out: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const next = lines[i + 1];
    out.push(line);
    if (next === undefined) break;
    if (next.trim() === "") continue;
    const curIsList = /^\s*([-*+]|\d+\.)\s/.test(line);
    const nextIsList = /^\s*([-*+]|\d+\.)\s/.test(next);
    if (curIsList && nextIsList) continue;
    if (/^\s*\|/.test(line) && /^\s*\|/.test(next)) continue;
    out.push("");
  }
  return out.join("\n");
};

const REGIME_META: Record<string, { label: string; color: string }> = {
  강세장: { label: "🟢 강세장", color: "green" },
  약세장: { label: "🔴 약세장", color: "red" },
  횡보장: { label: "🟡 횡보장", color: "gold" },
};

const FundManagerWidget = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [brief, setBrief] = useState("");
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefFailed, setBriefFailed] = useState(false);

  // 시장 국면
  const regimeQ = useQuery({
    queryKey: ["fund-manager", "regime"],
    queryFn: () => marketDeskApi.getRegime(),
    staleTime: 30 * 60_000,
  });

  // 오늘의 발굴 — 가치주 TOP5
  const screenQ = useQuery({
    queryKey: ["fund-manager", "screen", "value"],
    queryFn: () => screenerApi.runPreset("value", 5),
    staleTime: 30 * 60_000,
  });

  const loadBrief = async () => {
    setBriefLoading(true);
    setBriefFailed(false);
    setBrief("");
    try {
      const r = await apiClient.get<{ report: string }>(`/v1/insights/fund-manager-briefing`, {}, { timeout: 300_000 });
      setBrief(r.report ?? "");
      if (!r.report) message.warning("브리핑이 비어 있습니다");
    } catch {
      setBriefFailed(true);
      message.error("브리핑 생성 실패");
    } finally {
      setBriefLoading(false);
    }
  };

  const regime = regimeQ.data?.regime ?? "";
  const regimeMeta = REGIME_META[regime] ?? { label: `⚪ ${regime || "로딩"}`, color: "default" };
  const stocks = screenQ.data?.stocks?.slice(0, 5) ?? [];

  return (
    <Card
      size="small"
      style={{ borderTop: "2px solid #722ed1" }}
      title={
        <span>
          <RobotOutlined style={{ color: "#722ed1", marginRight: 6 }} />
          AI 펀드 매니저
        </span>
      }
      extra={
        <Tag color={regimeMeta.color} style={{ marginInlineEnd: 0 }}>
          {regimeMeta.label}
        </Tag>
      }
    >
      {/* 오늘의 발굴 */}
      <div style={{ marginBottom: 8 }}>
        <Typography.Text strong style={{ fontSize: 12 }}>
          🔍 오늘의 발굴 (가치주 TOP5)
        </Typography.Text>
        {screenQ.isLoading ? (
          <div style={{ textAlign: "center", padding: 12 }}>
            <Spin size="small" />
          </div>
        ) : stocks.length === 0 ? (
          <div style={{ fontSize: 12, color: "#888", padding: "6px 0" }}>조건에 맞는 종목 없음</div>
        ) : (
          <div style={{ marginTop: 4 }}>
            {stocks.map((s, i) => (
              <div
                key={s.ticker}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "4px 0",
                  borderBottom: i < stocks.length - 1 ? "1px solid rgba(128,128,128,0.12)" : "none",
                  fontSize: 12,
                }}
              >
                <span style={{ fontWeight: 600 }}>
                  {i + 1}. {s.name}
                </span>
                <span style={{ color: "#888" }}>
                  PBR {s.pbr?.toFixed(2) ?? "-"} · ROE {s.roe?.toFixed(1) ?? "-"}%
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 브리핑 토글 */}
      {!open && !briefLoading && (
        <Button size="small" block icon={<RobotOutlined />} onClick={() => { setOpen(true); loadBrief(); }}>
          오늘의 펀드매니저 브리핑 보기
        </Button>
      )}
      {briefLoading && (
        <div style={{ textAlign: "center", padding: 12 }}>
          <Spin size="small" /> <span style={{ fontSize: 12, color: "#888" }}>AI 브리핑 생성 중 (최대 90초)...</span>
        </div>
      )}
      {open && !briefLoading && briefFailed && !brief && (
        <div style={{ textAlign: "center", padding: 8 }}>
          <Typography.Paragraph style={{ fontSize: 12, color: "#888", marginBottom: 6 }}>
            브리핑 생성에 실패했어요.
          </Typography.Paragraph>
          <Button size="small" icon={<ReloadOutlined />} onClick={loadBrief}>
            다시 시도
          </Button>
        </div>
      )}
      {open && brief && (
        <div
          className="finance-analysis-markdown"
          style={{ background: "rgba(114,46,209,0.05)", borderRadius: 8, padding: "8px 12px", maxHeight: 300, overflow: "auto", fontSize: 12 }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{normalizeMarkdown(brief)}</ReactMarkdown>
          <div style={{ textAlign: "right", marginTop: 4 }}>
            <Button size="small" type="link" icon={<ReloadOutlined />} onClick={loadBrief}>재생성</Button>
          </div>
        </div>
      )}

      {/* 하단 링크 */}
      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
        <Button size="small" type="link" style={{ padding: 0 }} onClick={() => navigate("/research/market-desk")}>
          시장 데스크 <ArrowRightOutlined />
        </Button>
        <Button size="small" type="link" style={{ padding: 0 }} onClick={() => navigate("/research/screener")}>
          종목 발굴 <ArrowRightOutlined />
        </Button>
        <Button size="small" type="link" style={{ padding: 0 }} onClick={() => navigate("/investment/stocks?sub=rebalance")}>
          리밸런싱 <ArrowRightOutlined />
        </Button>
      </div>
    </Card>
  );
};

export default FundManagerWidget;
